// src/pages/Me/index.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Space,
  Typography,
  Avatar,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
} from "antd";
import dayjs from "dayjs";
import { updateCurrentUser } from "../../api/mockData";
import { useAuth } from "../../context/AuthContext";

function formatDate(value) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

export default function Me() {
  const { user: currentUser, setUser: setAuthUser } = useAuth();
  const [user, setUser] = useState(() => currentUser || {});
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    setUser(currentUser || {});
  }, [currentUser]);

  const safeUser = useMemo(
    () => ({
      username: user.username || "游客",
      email: user.email || "未设置邮箱",
      bio: user.bio || "写点喜欢的东西，记录生活与灵感。",
      role: user.role || "普通用户",
      createdAt: user.createdAt,
      avatar: user.avatar || user.avatarUrl || "",
    }),
    [user]
  );

  const joinedAt = formatDate(safeUser.createdAt);
  const avatarSrc = safeUser.avatar;

  const openEditor = () => {
    form.setFieldsValue({
      username: user.username || "",
      email: user.email || "",
      bio: user.bio || "",
      role: user.role || "",
      avatar: user.avatar || user.avatarUrl || "",
    });
    setEditing(true);
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("read failed"));
      reader.readAsDataURL(file);
    });

  const handleAvatarUpload = async (file) => {
    if (!file.type?.startsWith("image/")) {
      message.error("请上传图片文件");
      return Upload.LIST_IGNORE;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      form.setFieldsValue({ avatar: dataUrl });
    } catch (error) {
      message.error("头像读取失败，请重试");
    }
    return Upload.LIST_IGNORE;
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const next = updateCurrentUser(values);
    setUser(next);
    setAuthUser(next);
    setEditing(false);
  };

  return (
    <div className="me-page">
      <Card className="themed-card" bodyStyle={{ padding: "22px 26px" }}>
        <Space align="center" size={16} className="me-profile">
          <Avatar
            size={72}
            src={avatarSrc || undefined}
            style={{ background: "var(--accent-gradient)" }}
          >
            {safeUser.username.slice(0, 1).toUpperCase()}
          </Avatar>
          <div className="me-info">
            <Typography.Title
              level={4}
              style={{ margin: 0, color: "var(--text-primary)" }}
            >
              {safeUser.username}
            </Typography.Title>
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              {safeUser.bio}
            </Typography.Text>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button type="primary" className="themed-primary" onClick={openEditor}>
              编辑个人信息
            </Button>
          </div>
        </Space>
      </Card>

      <Card className="themed-card" bodyStyle={{ padding: "22px 26px" }}>
        <Space direction="vertical" size={12} className="me-list me-timeline">
          <div className="me-row">
            <Typography.Text className="me-row-label">用户名</Typography.Text>
            <Typography.Text className="me-row-value">
              {safeUser.username}
            </Typography.Text>
          </div>
          <div className="me-row">
            <Typography.Text className="me-row-label">邮箱</Typography.Text>
            <Typography.Text className="me-row-value">{safeUser.email}</Typography.Text>
          </div>
          <div className="me-row">
            <Typography.Text className="me-row-label">签名/简介</Typography.Text>
            <Typography.Text className="me-row-value">{safeUser.bio}</Typography.Text>
          </div>
          <div className="me-row">
            <Typography.Text className="me-row-label">角色</Typography.Text>
            <Typography.Text className="me-row-value">{safeUser.role}</Typography.Text>
          </div>
          <div className="me-row">
            <Typography.Text className="me-row-label">注册时间</Typography.Text>
            <Typography.Text className="me-row-value">{joinedAt}</Typography.Text>
          </div>
        </Space>
      </Card>

      <Modal
        title="编辑个人信息"
        open={editing}
        onCancel={() => setEditing(false)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="用户名"
            name="username"
            rules={[{ required: true, message: "请输入用户名" }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item label="邮箱" name="email">
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item label="签名/简介" name="bio">
            <Input.TextArea rows={3} placeholder="写点关于你的介绍" />
          </Form.Item>
          <Form.Item label="角色" name="role">
            <Input placeholder="如：普通用户 / 管理员" />
          </Form.Item>
          <Form.Item label="头像地址" name="avatar">
            <Input placeholder="请输入头像图片 URL" />
          </Form.Item>
          <Form.Item label="上传头像">
            <Upload.Dragger
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleAvatarUpload}
            >
              <p style={{ marginBottom: 8 }}>拖拽图片到这里，或点击上传</p>
              <p style={{ margin: 0, color: "rgba(0,0,0,0.45)" }}>
                上传后会自动填充到头像地址
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
